import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { Form, Col, Row } from 'react-bootstrap';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import If from '../../components/If';
import { CadernoLPMAAssinatura, CadernoLPMAColheita } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import { FaTrashAlt } from 'react-icons/fa';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import ComentarioExpandivel from './components/ComentarioExpandivel';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

const arquivoParaBase64 = (arquivo: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const resultado = reader.result;
			if (typeof resultado !== 'string') {
				reject(new Error('Falha ao converter arquivo para base64.'));
				return;
			}
			const base64 = resultado.includes(',') ? resultado.split(',')[1] : resultado;
			resolve(base64);
		};
		reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler arquivo.'));
		reader.readAsDataURL(arquivo);
	});

const MIME_PDF = 'application/pdf';
const MIME_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const validarArquivoPdfOuDocx = (arquivo: File): string | null => {
	const nome = (arquivo.name || '').toLowerCase();
	const okExt = nome.endsWith('.pdf') || nome.endsWith('.docx');
	const okMime =
		arquivo.type === MIME_PDF ||
		arquivo.type === MIME_DOCX ||
		arquivo.type === '' ||
		arquivo.type === 'application/octet-stream';
	if (!okExt) {
		return 'Selecione um arquivo PDF ou DOCX.';
	}
	if (!okMime) {
		return 'Tipo de arquivo não permitido. Use PDF ou DOCX.';
	}
	return null;
};

interface ColheitaModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAColheita) => void;
	dadosColheita?: CadernoLPMAColheita | null;
	/** Necessário para baixar anexo já salvo no servidor */
	cadernoLpmaId?: number | null;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

const defaultValues: CadernoLPMAColheita = {
	dataInicioColheita: null,
	dataTerminoColheita: null,
	responsavelColheita: '',
	tipoColheitaMecanica: false,
	tipoColheitaManual: false,
	detalhamentoColheitaJson: undefined,
	estadioFenologicoDestruicao: '',
	dataInicioIncorporacao: null,
	dataTerminoIncorporacao: null,
	dataInicioMonitoramento: null,
	tempoMonitoramentoQuatroMeses: false,
	tempoMonitoramentoSeisMeses: false,
	dataPrevisaoTerminoMonitoramento: null,
	dataTerminoMonitoramento: null,
	dataInicioComunicadoColheita: null,
	dataTerminoComunicadoColheita: null,
	dataInicioComunicadoMonitoramento: null,
	dataTerminoComunicadoMonitoramento: null,
	roupasColheita: false,
	observacoesRoupasColheita: '',
	materialIdentificado: false,
	observacoesMaterialIdentificado: '',
	equipamentosLimpos: false,
	observacoesEquipamentosLimpos: '',
	limpezaMaquinariosColheitaJson: undefined,
	destinoMaterialColhidoJson: undefined,
	observacoesDestinoMaterialColhido: '',
	observacoesColheita: '',
	cartaTransferenciaMaterialColhidoNomeArquivo: null,
	cartaTransferenciaMaterialColhidoContentType: null,
	cartaTransferenciaMaterialColhidoCaminhoBlob: null,
	cartaTransferenciaMaterialColhidoConteudoBase64: null,
	removerCartaTransferenciaMaterialColhidoAnexo: null,
	destinoMovimentacaoMesmaUnidade: false,
	destinoUnidadeMedidaMovimentacaoMesmaUnidade: 'kg',
	destinoQuantidadeMovimentacaoMesmaUnidade: '',
	destinoAmiMovimentacaoMesmaUnidade: '',
	destinoEnvioOutraUnidade: false,
	destinoUnidadeMedidaEnvioOutraUnidade: 'kg',
	destinoQuantidadeEnvioOutraUnidade: '',
	destinoAmiEnvioOutraUnidade: '',
	destinoDestruicaoAreaExperimental: false,
	destinoQuantidadeAreaExperimental: '',
	destinoDestruicaoAreaDescarteProprio: false,
	destinoQuantidadeAreaDescarteProprio: '',
	destinoAmiAreaDescarteProprio: '',
	destinoDestruicaoAreaOutroSite: false,
	destinoDescricaoOutroSite: '',
	destinoQuantidadeAreaOutroSite: '',
	destinoAmiAreaOutroSite: '',
	destinoOutro: false,
	destinoOutroDescricao: '',
	destinoOutroQuantidade: '',
	destinoOutroAmi: '',
	checklistMaterialEnvioPrevistoCTNBio: false,
	observacaoChecklistMaterialEnvioPrevistoCTNBio: '',
	checklistMaterialEnvioAmiCarta: false,
	observacaoChecklistMaterialEnvioAmiCarta: '',
	checklistMaterialEnvioDuplaEmbalagem: false,
	observacaoChecklistMaterialEnvioDuplaEmbalagem: '',
	checklistMaterialEnvioEmbalagemIdentificada: false,
	observacaoChecklistMaterialEnvioEmbalagemIdentificada: '',
	checklistMaterialArmazenamentoPrevistoCTNBio: false,
	observacaoChecklistMaterialArmazenamentoPrevistoCTNBio: '',
	checklistMaterialArmazenamentoLocalCQB: false,
	observacaoChecklistMaterialArmazenamentoLocalCQB: '',
	checklistMaterialArmazenamentoDuplaEmbalagem: false,
	observacaoChecklistMaterialArmazenamentoDuplaEmbalagem: '',
	checklistMaterialArmazenamentoInventario: false,
	observacaoChecklistMaterialArmazenamentoInventario: '',
	checklistMaterialArmazenamentoIdentificado: false,
	observacaoChecklistMaterialArmazenamentoIdentificado: '',
	checklistMaterialDestruidoProcedimentoCTNBio: false,
	observacaoChecklistMaterialDestruidoProcedimentoCTNBio: '',
	checklistMaterialDestruidoLocalCQB: false,
	observacaoChecklistMaterialDestruidoLocalCQB: '',
	checklistMaterialDestruidoFormularioPreenchido: false,
	observacaoChecklistMaterialDestruidoFormularioPreenchido: '',
};

type AbaColheitaId =
	| 'colheita-form-1'
	| 'colheita-checklist-1'
	| 'colheita-material-1';

const ABAS_COLHEITA: Array<{ id: AbaColheitaId; label: string }> = [
	{ id: 'colheita-form-1', label: 'Formulário 1' },
	{ id: 'colheita-checklist-1', label: 'Checklist 1' },
	{ id: 'colheita-material-1', label: 'Material 1' },
];

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

const urlObterAmiPorNumeroAutorizacaoCibio = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?NumeroAutorizacaoCibio=${encodeURIComponent(texto)}`;

const urlObterAmiPorCodigo = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?Codigo=${encodeURIComponent(texto)}`;

const candidatosCodigoAmi = (raw: string): string[] => {
	const t = raw.trim();
	if (!t) return [];
	const out: string[] = [];
	const push = (s: string) => {
		const x = s.trim();
		if (x && !out.includes(x)) out.push(x);
	};
	push(t);
	push(t.replace(/\//g, '.'));
	push(t.replace(/\./g, '/'));
	if (!/^AMI\s/i.test(t)) {
		push(`AMI ${t}`);
		push(`AMI ${t.replace(/\//g, '.')}`);
	}
	return out;
};

const formatarAmiComBarra = (valor: string): string => {
	const digitos = (valor || '').replace(/\D/g, '').slice(0, 7);
	if (digitos.length <= 3) return digitos;
	return `${digitos.slice(0, 3)}/${digitos.slice(3)}`;
};

export default function ColheitaModal({
	show,
	onHide,
	onSubmit,
	dadosColheita,
	cadernoLpmaId,
	assinaturasDisponiveis = [],
}: ColheitaModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		getValues,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CadernoLPMAColheita>({
		mode: 'all',
		defaultValues,
		shouldUnregister: false,
	});

	const [abaColheitaAtiva, setAbaColheitaAtiva] = useState<AbaColheitaId>('colheita-form-1');

	// Estados para assinaturas - Formulário 1
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	const [arquivoComunicadoForm1, setArquivoComunicadoForm1] = useState<File | null>(null);
	const [erroArquivoComunicado1, setErroArquivoComunicado1] = useState('');
	const [removerAnexoComunicado1, setRemoverAnexoComunicado1] = useState(false);
	const [arquivoCartaTransferencia, setArquivoCartaTransferencia] = useState<File | null>(null);
	const [erroArquivoCartaTransferencia, setErroArquivoCartaTransferencia] = useState('');
	const [removerAnexoCartaTransferencia, setRemoverAnexoCartaTransferencia] = useState(false);
	const [loadingAmiLink, setLoadingAmiLink] = useState<Record<string, boolean>>({});

	const idColheitaEdicao = dadosColheita?.id ?? (dadosColheita as { Id?: number } | undefined)?.Id ?? 0;
	const idCadernoParaDownload = cadernoLpmaId ?? dadosColheita?.cadernoLPMAId ?? (dadosColheita as { CadernoLPMAId?: number } | undefined)?.CadernoLPMAId ?? 0;

	const baixarAnexoComunicadoCibio = async () => {
		if (!idCadernoParaDownload || !idColheitaEdicao) {
			return;
		}
		try {
			const resposta = await api.get(
				`api/v1/cadernoLPMA/cadernoLPMA/${idCadernoParaDownload}/colheitas/${idColheitaEdicao}/anexo-comunicado-cibio-externo`,
				{ responseType: 'blob' },
			);
			const nome = dadosColheita?.comunicadoCibioOficiosExternosNomeArquivo ?? `anexo-comunicado-${idColheitaEdicao}`;
			const contentType = dadosColheita?.comunicadoCibioOficiosExternosContentType ?? 'application/octet-stream';
			const blob = new Blob([resposta.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = nome;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Erro ao baixar anexo:', error);
		}
	};

	const baixarAnexoCartaTransferencia = async () => {
		if (!idCadernoParaDownload || !idColheitaEdicao) {
			return;
		}
		try {
			const resposta = await api.get(
				`api/v1/cadernoLPMA/cadernoLPMA/${idCadernoParaDownload}/colheitas/${idColheitaEdicao}/carta-transferencia-material-colhido`,
				{ responseType: 'blob' },
			);
			const nome =
				dadosColheita?.cartaTransferenciaMaterialColhidoNomeArquivo ??
				`carta-transferencia-${idColheitaEdicao}`;
			const contentType =
				dadosColheita?.cartaTransferenciaMaterialColhidoContentType ?? 'application/octet-stream';
			const blob = new Blob([resposta.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = nome;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Erro ao baixar anexo:', error);
		}
	};

	const tentarObterAmiPorIdNumerico = async (raw: string): Promise<number | null> => {
		const t = raw.trim();
		if (!/^\d+$/.test(t)) return null;
		const id = parseInt(t, 10);
		if (!Number.isFinite(id) || id <= 0) return null;
		try {
			const { data, status } = await api.get<{ id?: number }>(`api/v1/autorizacoes-cibio/MovimentacaoInterna/${id}`);
			if (status === 200 && typeof data?.id === 'number') return data.id;
		} catch {
		}
		return null;
	};

	const abrirAmiDigitada = async (campo: string, valorBruto: string) => {
		const valor = (valorBruto || '').trim();
		if (!valor) return;
		setLoadingAmiLink((prev) => ({ ...prev, [campo]: true }));
		try {
			let id = await tentarObterAmiPorIdNumerico(valor);
			if (!id) {
				try {
					const { data } = await api.get(urlObterAmiPorNumeroAutorizacaoCibio(valor));
					id = Array.isArray(data) ? data[0]?.id : data?.id;
				} catch {
				}
			}
			if (!id) {
				for (const candidato of candidatosCodigoAmi(valor)) {
					try {
						const { data } = await api.get(urlObterAmiPorCodigo(candidato));
						id = Array.isArray(data) ? data[0]?.id : data?.id;
						if (id) break;
					} catch {
					}
				}
			}
			if (id) {
				window.open(`/autorizacoes/visualizar/ami?id=${id}`, '_blank');
			}
		} finally {
			setLoadingAmiLink((prev) => ({ ...prev, [campo]: false }));
		}
	};

	// Carregar assinatura do usuário
	useEffect(() => {
		const carregarAssinaturaUsuario = async () => {
			try {
				const token = localStorage.getItem('token');
				if (token) {
					const decoded: any = JSON.parse(atob(token.split('.')[1]));
					const username = decoded.unique_name || '';
					
					const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
					if (data && data.assinaturaBase64) {
						setAssinaturaUsuario(data.assinaturaBase64);
					}
				}
			} catch (error) {
				console.error('Erro ao carregar assinatura:', error);
			}
		};

		if (show) {
			carregarAssinaturaUsuario();
			
			// Carregar assinaturas salvas se existirem
			if (dadosColheita?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(dadosColheita.assinaturaEnvolvidosBase64);
			} else {
				setAssinaturaEnvolvidos(null);
			}

			setNomesAdicionais(parseNomesEnvolvidosJson(dadosColheita?.nomesEnvolvidosAdicionaisJson));
		} else {
			// Limpar estados ao fechar
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		show,
		dadosColheita?.assinaturaEnvolvidosBase64,
		dadosColheita?.nomesEnvolvidosAdicionaisJson,
	]);

	const [quantidadeLinhasDetalhamento, setQuantidadeLinhasDetalhamento] = useState<number>(1);
	const [quantidadeLinhasLimpeza, setQuantidadeLinhasLimpeza] = useState<number>(1);
	const [quantidadeLinhasLimpeza2, setQuantidadeLinhasLimpeza2] = useState<number>(1);
	const [quantidadeLinhasDestino, setQuantidadeLinhasDestino] = useState<number>(1);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setQuantidadeLinhasDetalhamento(1);
			setQuantidadeLinhasLimpeza(1);
			setQuantidadeLinhasLimpeza2(1);
			setQuantidadeLinhasDestino(1);
			setAbaColheitaAtiva('colheita-form-1');
			setArquivoComunicadoForm1(null);
			setErroArquivoComunicado1('');
			setRemoverAnexoComunicado1(false);
			setArquivoCartaTransferencia(null);
			setErroArquivoCartaTransferencia('');
			setRemoverAnexoCartaTransferencia(false);
		} else if (dadosColheita) {
			setArquivoComunicadoForm1(null);
			setErroArquivoComunicado1('');
			setRemoverAnexoComunicado1(false);
			setArquivoCartaTransferencia(null);
			setErroArquivoCartaTransferencia('');
			setRemoverAnexoCartaTransferencia(false);
			// Modo edição - popular formulário com dados existentes
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			// Parse dos JSONs
			let detalhamentoColheita: any[] = [];
			let limpezaMaquinarios: any[] = [];
			let limpezaMaquinarios2: any[] = [];
			let destinoMaterialParsed: any = null;

			try {
				if (dadosColheita.detalhamentoColheitaJson) {
					detalhamentoColheita = JSON.parse(dadosColheita.detalhamentoColheitaJson);
				}
			} catch (e) {}

			try {
				if (dadosColheita.limpezaMaquinariosColheitaJson) {
					limpezaMaquinarios = JSON.parse(dadosColheita.limpezaMaquinariosColheitaJson);
				}
			} catch (e) {}

			try {
				if ((dadosColheita as any).limpezaMaquinariosColheita2Json) {
					limpezaMaquinarios2 = JSON.parse((dadosColheita as any).limpezaMaquinariosColheita2Json);
				}
			} catch (e) {}

			try {
				if (dadosColheita.destinoMaterialColhidoJson) {
					destinoMaterialParsed = JSON.parse(dadosColheita.destinoMaterialColhidoJson);
				}
			} catch (e) {}

			// Atualiza quantidade de linhas
			setQuantidadeLinhasDetalhamento(Math.max(1, detalhamentoColheita.length));
			setQuantidadeLinhasLimpeza(Math.max(1, limpezaMaquinarios.length));
			setQuantidadeLinhasLimpeza2(Math.max(1, limpezaMaquinarios2.length));
			setQuantidadeLinhasDestino(Math.max(1, destinoMaterialParsed?.itens?.length || 0));

			// Prepara dados para resetar o formulário
			const dadosParaReset: any = {
				...dadosColheita,
				dataInicioColheita: parseDate(dadosColheita.dataInicioColheita),
				dataTerminoColheita: parseDate(dadosColheita.dataTerminoColheita),
				dataInicioIncorporacao: parseDate(dadosColheita.dataInicioIncorporacao),
				dataTerminoIncorporacao: parseDate(dadosColheita.dataTerminoIncorporacao),
				dataInicioMonitoramento: parseDate(dadosColheita.dataInicioMonitoramento),
				dataPrevisaoTerminoMonitoramento: parseDate(dadosColheita.dataPrevisaoTerminoMonitoramento),
				dataTerminoMonitoramento: parseDate(dadosColheita.dataTerminoMonitoramento),
				dataInicioComunicadoColheita: parseDate(dadosColheita.dataInicioComunicadoColheita),
				dataTerminoComunicadoColheita: parseDate(dadosColheita.dataTerminoComunicadoColheita),
				dataInicioComunicadoMonitoramento: parseDate(dadosColheita.dataInicioComunicadoMonitoramento),
				dataTerminoComunicadoMonitoramento: parseDate(dadosColheita.dataTerminoComunicadoMonitoramento),
			};

			// Popular campos de detalhamento colheita 1
			detalhamentoColheita.forEach((item: any, index: number) => {
				dadosParaReset[`detalhamentoTalhao${index}`] = item.talhao || '';
				dadosParaReset[`detalhamentoEnsaio${index}`] = item.ensaioExperimento || '';
				dadosParaReset[`detalhamentoStatus${index}`] = item.situacao || '';
				dadosParaReset[`detalhamentoDataInicio${index}`] = parseDate(item.dataInicio);
				dadosParaReset[`detalhamentoDataTermino${index}`] = parseDate(item.dataTermino);
			});

			// Popular campos de limpeza 1
			limpezaMaquinarios.forEach((item: any, index: number) => {
				const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(item.metodo);
				dadosParaReset[`limpezaNome${index}`] = item.nome || '';
				dadosParaReset[`limpezaDataEntrada${index}`] = parseDate(item.dataEntrada);
				dadosParaReset[`limpezaDataSaida${index}`] = parseDate(item.dataSaida);
				dadosParaReset[`limpezaMetodo${index}`] = metodoLimpeza.metodo;
				dadosParaReset[`limpezaMetodoOutros${index}`] = metodoLimpeza.outros;
				dadosParaReset[`limpezaResponsavel${index}`] = item.responsavel || '';
			});

			limpezaMaquinarios2.forEach((item: any, index: number) => {
				const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(item.metodo);
				dadosParaReset[`limpeza2Nome${index}`] = item.nome || '';
				dadosParaReset[`limpeza2DataEntradaAntes${index}`] = parseDate(item.dataEntrada);
				dadosParaReset[`limpeza2DataSaidaAntes${index}`] = parseDate(item.dataSaida);
				dadosParaReset[`limpeza2Metodo${index}`] = metodoLimpeza.metodo;
				dadosParaReset[`limpeza2MetodoOutros${index}`] = metodoLimpeza.outros;
				dadosParaReset[`limpeza2Responsavel${index}`] = item.responsavel || '';
			});

			// Popular campos de destino 1
			if (destinoMaterialParsed) {
				const opcoes = destinoMaterialParsed.opcoes || {};
				dadosParaReset.destinoMovimentacaoMesmaUnidade = opcoes.movimentacaoMesmaUnidade || false;
				dadosParaReset.destinoUnidadeMedidaMovimentacaoMesmaUnidade = opcoes.unidadeMedidaMovimentacaoMesmaUnidade || 'kg';
				dadosParaReset.destinoQuantidadeMovimentacaoMesmaUnidade = opcoes.quantidadeMovimentacaoMesmaUnidade || '';
				dadosParaReset.destinoAmiMovimentacaoMesmaUnidade = opcoes.amiMovimentacaoMesmaUnidade || '';
				dadosParaReset.destinoEnvioOutraUnidade = opcoes.envioOutraUnidade || false;
				dadosParaReset.destinoUnidadeMedidaEnvioOutraUnidade = opcoes.unidadeMedidaEnvioOutraUnidade || 'kg';
				dadosParaReset.destinoQuantidadeEnvioOutraUnidade = opcoes.quantidadeEnvioOutraUnidade || '';
				dadosParaReset.destinoAmiEnvioOutraUnidade = opcoes.amiEnvioOutraUnidade || '';
				dadosParaReset.destinoDestruicaoAreaExperimental = opcoes.destruicaoAreaExperimental || false;
				dadosParaReset.destinoQuantidadeAreaExperimental = opcoes.quantidadeAreaExperimental || '';
				dadosParaReset.destinoDestruicaoAreaDescarteProprio = opcoes.destruicaoAreaDescarteProprio || false;
				dadosParaReset.destinoQuantidadeAreaDescarteProprio = opcoes.quantidadeAreaDescarteProprio || '';
				dadosParaReset.destinoAmiAreaDescarteProprio = opcoes.amiAreaDescarteProprio || '';
				dadosParaReset.destinoDestruicaoAreaOutroSite = opcoes.destruicaoAreaOutroSite || false;
				dadosParaReset.destinoDescricaoOutroSite = opcoes.descricaoOutroSite || '';
				dadosParaReset.destinoQuantidadeAreaOutroSite = opcoes.quantidadeAreaOutroSite || '';
				dadosParaReset.destinoAmiAreaOutroSite = opcoes.amiAreaOutroSite || '';
				dadosParaReset.destinoOutro = opcoes.outroDestino || false;
				dadosParaReset.destinoOutroDescricao = opcoes.outroDescricao || '';
				dadosParaReset.destinoOutroQuantidade = opcoes.outroQuantidade || '';
				dadosParaReset.destinoOutroAmi = opcoes.outroAmi || '';

				const itens = destinoMaterialParsed.itens || [];
				itens.forEach((item: any, index: number) => {
					dadosParaReset[`destinoData${index}`] = parseDate(item.data);
					dadosParaReset[`destinoTalhao${index}`] = item.talhao || '';
					dadosParaReset[`destinoEnsaio${index}`] = item.ensaioExperimento || '';
					dadosParaReset[`destinoNumeroAmostras${index}`] = item.numeroAmostras || '';
					dadosParaReset[`destinoQuantidadeKg${index}`] = item.quantidadeKg || '';
				});
			}

			reset(dadosParaReset);
		} else if (show) {
			setArquivoComunicadoForm1(null);
			setErroArquivoComunicado1('');
			setRemoverAnexoComunicado1(false);
			setArquivoCartaTransferencia(null);
			setErroArquivoCartaTransferencia('');
			setRemoverAnexoCartaTransferencia(false);
		}
	}, [show, dadosColheita, reset]);

	const adicionarLinhaDetalhamento = () => setQuantidadeLinhasDetalhamento((prev) => prev + 1);
	const removerLinhaDetalhamento = (index: number) => {
		if (quantidadeLinhasDetalhamento === 1) return;
		setQuantidadeLinhasDetalhamento((prev) => prev - 1);
		setValue(`detalhamentoTalhao${index}` as any, undefined);
		setValue(`detalhamentoEnsaio${index}` as any, undefined);
		setValue(`detalhamentoStatus${index}` as any, undefined);
		setValue(`detalhamentoDataInicio${index}` as any, null);
		setValue(`detalhamentoDataTermino${index}` as any, null);
	};

	const adicionarLinhaLimpeza = () => setQuantidadeLinhasLimpeza((prev) => prev + 1);
	const removerLinhaLimpeza = (index: number) => {
		if (quantidadeLinhasLimpeza === 1) return;
		setQuantidadeLinhasLimpeza((prev) => prev - 1);
		setValue(`limpezaNome${index}` as any, undefined);
		setValue(`limpezaDataEntrada${index}` as any, null);
		setValue(`limpezaDataSaida${index}` as any, null);
		setValue(`limpezaMetodo${index}` as any, undefined);
		setValue(`limpezaMetodoOutros${index}` as any, undefined);
		setValue(`limpezaResponsavel${index}` as any, undefined);
	};

	const adicionarLinhaLimpeza2 = () => setQuantidadeLinhasLimpeza2((prev) => prev + 1);
	const removerLinhaLimpeza2 = (index: number) => {
		if (quantidadeLinhasLimpeza2 === 1) return;
		setQuantidadeLinhasLimpeza2((prev) => prev - 1);
		setValue(`limpeza2Nome${index}` as any, undefined);
		setValue(`limpeza2DataEntradaAntes${index}` as any, null);
		setValue(`limpeza2DataSaidaAntes${index}` as any, null);
		setValue(`limpeza2Metodo${index}` as any, undefined);
		setValue(`limpeza2MetodoOutros${index}` as any, undefined);
		setValue(`limpeza2Responsavel${index}` as any, undefined);
	};

	const adicionarLinhaDestino = () => setQuantidadeLinhasDestino((prev) => prev + 1);
	const removerLinhaDestino = (index: number) => {
		if (quantidadeLinhasDestino === 1) return;
		setQuantidadeLinhasDestino((prev) => prev - 1);
		setValue(`destinoData${index}` as any, null);
		setValue(`destinoTalhao${index}` as any, undefined);
		setValue(`destinoEnsaio${index}` as any, undefined);
		setValue(`destinoNumeroAmostras${index}` as any, undefined);
		setValue(`destinoQuantidadeKg${index}` as any, undefined);
	};

	const handleSubmitColheita = async (dadosForm: CadernoLPMAColheita) => {
		if (erroArquivoComunicado1 || erroArquivoCartaTransferencia) {
			return;
		}
		const valores = getValues() as Record<string, any>;
		const dados = { ...dadosForm } as Record<string, any>;

		const detalhamento: any[] = [];
		for (let i = 0; i < quantidadeLinhasDetalhamento; i++) {
			const talhao = valores[`detalhamentoTalhao${i}`];
			const ensaio = valores[`detalhamentoEnsaio${i}`];
			const status = valores[`detalhamentoStatus${i}`];
			const dataInicio = valores[`detalhamentoDataInicio${i}`];
			const dataTermino = valores[`detalhamentoDataTermino${i}`];

			if (talhao || ensaio || status || dataInicio || dataTermino) {
				detalhamento.push({
					talhao,
					ensaioExperimento: ensaio,
					situacao: status,
					dataInicio: formatarDataParaJson(dataInicio),
					dataTermino: formatarDataParaJson(dataTermino),
				});
			}

			delete dados[`detalhamentoTalhao${i}`];
			delete dados[`detalhamentoEnsaio${i}`];
			delete dados[`detalhamentoStatus${i}`];
			delete dados[`detalhamentoDataInicio${i}`];
			delete dados[`detalhamentoDataTermino${i}`];
		}
		dados.detalhamentoColheitaJson = detalhamento.length ? JSON.stringify(detalhamento) : null;

		// Se as datas gerais não foram preenchidas, deriva do detalhamento (menor início / maior término)
		const datasInicioDetalhamento = detalhamento
			.map((item) => (item.dataInicio ? new Date(item.dataInicio) : null))
			.filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));
		const datasTerminoDetalhamento = detalhamento
			.map((item) => (item.dataTermino ? new Date(item.dataTermino) : null))
			.filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));

		if (!dados.dataInicioColheita && datasInicioDetalhamento.length) {
			dados.dataInicioColheita = new Date(Math.min(...datasInicioDetalhamento.map((d) => d.getTime())));
		}
		if (!dados.dataTerminoColheita && datasTerminoDetalhamento.length) {
			dados.dataTerminoColheita = new Date(Math.max(...datasTerminoDetalhamento.map((d) => d.getTime())));
		}

		const limpeza: any[] = [];
		for (let i = 0; i < quantidadeLinhasLimpeza; i++) {
			const nome = valores[`limpezaNome${i}`];
			const dataEntrada = valores[`limpezaDataEntrada${i}`];
			const dataSaida = valores[`limpezaDataSaida${i}`];
			const metodo = formatarMetodoLimpezaParaJson(
				valores[`limpezaMetodo${i}`],
				valores[`limpezaMetodoOutros${i}`],
			);
			const responsavel = valores[`limpezaResponsavel${i}`];

			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				limpeza.push({
					nome,
					dataEntrada: formatarDataParaJson(dataEntrada),
					dataSaida: formatarDataParaJson(dataSaida),
					metodo,
					responsavel,
				});
			}

			delete dados[`limpezaNome${i}`];
			delete dados[`limpezaDataEntrada${i}`];
			delete dados[`limpezaDataSaida${i}`];
			delete dados[`limpezaMetodo${i}`];
			delete dados[`limpezaMetodoOutros${i}`];
			delete dados[`limpezaResponsavel${i}`];
		}
		dados.limpezaMaquinariosColheitaJson = limpeza.length ? JSON.stringify(limpeza) : null;

		const limpeza2: any[] = [];
		for (let i = 0; i < quantidadeLinhasLimpeza2; i++) {
			const nome = valores[`limpeza2Nome${i}`];
			const dataEntrada = valores[`limpeza2DataEntradaAntes${i}`];
			const dataSaida = valores[`limpeza2DataSaidaAntes${i}`];
			const metodo = formatarMetodoLimpezaParaJson(
				valores[`limpeza2Metodo${i}`],
				valores[`limpeza2MetodoOutros${i}`],
			);
			const responsavel = valores[`limpeza2Responsavel${i}`];

			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				limpeza2.push({
					nome,
					dataEntrada: formatarDataParaJson(dataEntrada),
					dataSaida: formatarDataParaJson(dataSaida),
					metodo,
					responsavel,
				});
			}

			delete dados[`limpeza2Nome${i}`];
			delete dados[`limpeza2DataEntradaAntes${i}`];
			delete dados[`limpeza2DataSaidaAntes${i}`];
			delete dados[`limpeza2Metodo${i}`];
			delete dados[`limpeza2MetodoOutros${i}`];
			delete dados[`limpeza2Responsavel${i}`];
		}
		(dados as any).limpezaMaquinariosColheita2Json = limpeza2.length ? JSON.stringify(limpeza2) : null;

		const destinoMaterial: any[] = [];
		for (let i = 0; i < quantidadeLinhasDestino; i++) {
			const data = valores[`destinoData${i}`];
			const talhao = valores[`destinoTalhao${i}`];
			const ensaio = valores[`destinoEnsaio${i}`];
			const numeroAmostras = valores[`destinoNumeroAmostras${i}`];
			const quantidadeKg = valores[`destinoQuantidadeKg${i}`];

			if (data || talhao || ensaio || numeroAmostras || quantidadeKg) {
				destinoMaterial.push({
					data: formatarDataParaJson(data),
					talhao,
					ensaioExperimento: ensaio,
					numeroAmostras,
					quantidadeKg,
				});
			}

			delete dados[`destinoData${i}`];
			delete dados[`destinoTalhao${i}`];
			delete dados[`destinoEnsaio${i}`];
			delete dados[`destinoNumeroAmostras${i}`];
			delete dados[`destinoQuantidadeKg${i}`];
		}
		dados.destinoMaterialColhidoJson = destinoMaterial.length ? JSON.stringify(destinoMaterial) : null;

		const destinosSelecionados = {
			movimentacaoMesmaUnidade: valores.destinoMovimentacaoMesmaUnidade,
			unidadeMedidaMovimentacaoMesmaUnidade: valores.destinoUnidadeMedidaMovimentacaoMesmaUnidade || 'kg',
			quantidadeMovimentacaoMesmaUnidade: valores.destinoQuantidadeMovimentacaoMesmaUnidade,
			amiMovimentacaoMesmaUnidade: valores.destinoAmiMovimentacaoMesmaUnidade,
			envioOutraUnidade: valores.destinoEnvioOutraUnidade,
			unidadeMedidaEnvioOutraUnidade: valores.destinoUnidadeMedidaEnvioOutraUnidade || 'kg',
			quantidadeEnvioOutraUnidade: valores.destinoQuantidadeEnvioOutraUnidade,
			amiEnvioOutraUnidade: valores.destinoAmiEnvioOutraUnidade,
			destruicaoAreaExperimental: valores.destinoDestruicaoAreaExperimental,
			quantidadeAreaExperimental: valores.destinoQuantidadeAreaExperimental,
			destruicaoAreaDescarteProprio: valores.destinoDestruicaoAreaDescarteProprio,
			quantidadeAreaDescarteProprio: valores.destinoQuantidadeAreaDescarteProprio,
			amiAreaDescarteProprio: valores.destinoAmiAreaDescarteProprio,
			destruicaoAreaOutroSite: valores.destinoDestruicaoAreaOutroSite,
			descricaoOutroSite: valores.destinoDescricaoOutroSite,
			quantidadeAreaOutroSite: valores.destinoQuantidadeAreaOutroSite,
			amiAreaOutroSite: valores.destinoAmiAreaOutroSite,
			outroDestino: valores.destinoOutro,
			outroDescricao: valores.destinoOutroDescricao,
			outroQuantidade: valores.destinoOutroQuantidade,
			outroAmi: valores.destinoOutroAmi,
		};
		dados.destinoMaterialColhidoJson = JSON.stringify({
			itens: destinoMaterial,
			opcoes: destinosSelecionados,
			estadioFenologico: dados.estadioFenologicoDestruicao,
		});

		delete dados.destinoMovimentacaoMesmaUnidade;
		delete dados.destinoUnidadeMedidaMovimentacaoMesmaUnidade;
		delete dados.destinoQuantidadeMovimentacaoMesmaUnidade;
		delete dados.destinoAmiMovimentacaoMesmaUnidade;
		delete dados.destinoEnvioOutraUnidade;
		delete dados.destinoUnidadeMedidaEnvioOutraUnidade;
		delete dados.destinoQuantidadeEnvioOutraUnidade;
		delete dados.destinoAmiEnvioOutraUnidade;
		delete dados.destinoDestruicaoAreaExperimental;
		delete dados.destinoQuantidadeAreaExperimental;
		delete dados.destinoDestruicaoAreaDescarteProprio;
		delete dados.destinoQuantidadeAreaDescarteProprio;
		delete dados.destinoAmiAreaDescarteProprio;
		delete dados.destinoDestruicaoAreaOutroSite;
		delete dados.destinoDescricaoOutroSite;
		delete dados.destinoQuantidadeAreaOutroSite;
		delete dados.destinoAmiAreaOutroSite;
		delete dados.destinoOutro;
		delete dados.destinoOutroDescricao;
		delete dados.destinoOutroQuantidade;
		delete dados.destinoOutroAmi;

		// Adicionar assinaturas aos dados
		const dadosComAssinaturas: CadernoLPMAColheita = {
			...dados,
			assinaturaEnvolvidosBase64: assinaturaEnvolvidos || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
		};

		if (removerAnexoComunicado1) {
			dadosComAssinaturas.removerComunicadoCibioOficiosExternosAnexo = true;
		} else if (arquivoComunicadoForm1) {
			dadosComAssinaturas.comunicadoCibioOficiosExternosConteudoBase64 = await arquivoParaBase64(arquivoComunicadoForm1);
			dadosComAssinaturas.comunicadoCibioOficiosExternosNomeArquivo = arquivoComunicadoForm1.name;
			dadosComAssinaturas.comunicadoCibioOficiosExternosContentType =
				arquivoComunicadoForm1.type || MIME_PDF;
		}

		if (removerAnexoCartaTransferencia) {
			dadosComAssinaturas.removerCartaTransferenciaMaterialColhidoAnexo = true;
		} else if (arquivoCartaTransferencia) {
			dadosComAssinaturas.cartaTransferenciaMaterialColhidoConteudoBase64 = await arquivoParaBase64(arquivoCartaTransferencia);
			dadosComAssinaturas.cartaTransferenciaMaterialColhidoNomeArquivo = arquivoCartaTransferencia.name;
			dadosComAssinaturas.cartaTransferenciaMaterialColhidoContentType =
				arquivoCartaTransferencia.type || MIME_DOCX;
		}

		onSubmit(dadosComAssinaturas);
		onHide();
	};

	const renderizadorData = (nome: string) => (
		<Controller
			control={control}
			name={nome as any}
			render={({ onChange, value }) => (
				<DatePicker
					selected={value || null}
					onChange={onChange}
					className="input-custom-to-date"
					locale="pt-BR"
					dateFormat="dd/MM/yyyy"
					placeholderText="Selecionar a data"
				/>
			)}
		/>
	);

	const renderizadorAmiComLink = (nome: string, placeholder: string, maxWidth = 200) => {
		const valor = watch(nome as any) as string | undefined;
		const valorTrim = (valor || '').trim();
		return (
			<div style={{ maxWidth }}>
				<Controller
					control={control}
					name={nome as any}
					render={({ onChange, value }) => (
						<Form.Control
							value={value || ''}
							placeholder={placeholder}
							onChange={(e) => onChange(formatarAmiComBarra(e.target.value))}
						/>
					)}
				/>
				{valorTrim ? (
					<button
						type="button"
						className="btn btn-link p-0 mt-1"
						disabled={!!loadingAmiLink[nome]}
						onClick={() => void abrirAmiDigitada(nome, valorTrim)}
					>
						{loadingAmiLink[nome] ? 'Buscando AMI...' : `Visualizar AMI ${valorTrim}`}
					</button>
				) : null}
			</div>
		);
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.COLHEITAS_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitColheita)} autoComplete="off">
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{ABAS_COLHEITA.map((aba) => (
								<Button
									key={aba.id}
									title={aba.label}
									contained={abaColheitaAtiva === aba.id}
									className={abaColheitaAtiva === aba.id ? 'active' : ''}
									type="button"
									onClick={() => setAbaColheitaAtiva(aba.id)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				{abaColheitaAtiva === 'colheita-form-1' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h2>Formulário de Colheita, Destruição e início de Monitoramento - 1</h2>
						</Form.Label>
						<p>Este formulário deve ser utilizado para registrar a colheita da LPMA assim como os ensaios não colhidos (destruídos).</p>
					</Form.Group>
				</Form.Row>

				

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Registro dos envolvidos e datas inicial/final de colheita/destruição da LPMA</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data início colheita</Form.Label>
						{renderizadorData('dataInicioColheita')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Data término colheita</Form.Label>
						{renderizadorData('dataTerminoColheita')}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Responsável</Form.Label>
						<Form.Control
							ref={register}
							name="responsavelColheita"
							placeholder="Nome do responsável"
						/>
					</Form.Group>
				</Form.Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
					assinaturasDisponiveis={assinaturasDisponiveis}
				/>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Detalhamento da colheita/destruição</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={2}>Talhão</Col>
					<Col md={3}>Ensaio/Experimento</Col>
					<Col md={2}>Situação</Col>
					<Col md={2}>Início</Col>
					<Col md={2}>Término</Col>
					<Col md={1}></Col>
				</Row>

				{Array.from({ length: quantidadeLinhasDetalhamento }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`linha-detalhamento-${index}`}>
						<Col md={2}>
							<Form.Control
								ref={register}
								name={`detalhamentoTalhao${index}`}
								placeholder="Talhão"
							/>
						</Col>
						<Col md={3}>
							<Form.Control
								ref={register}
								name={`detalhamentoEnsaio${index}`}
								placeholder="Ensaio/Experimento"
							/>
						</Col>
						<Col md={2}>
							<Form.Control as="select" ref={register} name={`detalhamentoStatus${index}`} defaultValue="">
								<option value="">Selecionar</option>
								<option value="1">1 - Colhido</option>
								<option value="2">2 - Destruído</option>
							</Form.Control>
						</Col>
						<Col md={2}>
							{renderizadorData(`detalhamentoDataInicio${index}`)}
						</Col>
						<Col md={2}>
							{renderizadorData(`detalhamentoDataTermino${index}`)}
						</Col>
						<Col md={1} className="d-flex justify-content-end">
							{quantidadeLinhasDetalhamento > 1 && (
								<Button
									ariaLabel="Remover"
									contained={false}
									type="button"
									className="btn-icon-only"
									onClick={() => removerLinhaDetalhamento(index)}
								>
									<FaTrashAlt aria-hidden size={18} />
								</Button>
							)}
						</Col>
					</Row>
				))}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar linha" contained={false} type="button" onClick={adicionarLinhaDetalhamento} />
				</div>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label>Tipo de colheita/destruição realizada</Form.Label>
						<div className="d-flex" style={{ gap: '24px' }}>
							<Form.Check
								type="checkbox"
								ref={register}
								name="tipoColheitaMecanica"
								label="Mecânica"
							/>
							<Form.Check
								type="checkbox"
								ref={register}
								name="tipoColheitaManual"
								label="Manual"
							/>
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Em caso de destruição antes da colheita, registre o estádio fenológico da cultura</Form.Label>
						<Form.Control
							ref={register}
							name="estadioFenologicoDestruicao"
							placeholder="Descreva o estádio fenológico"
						/>
					</Form.Group>
				</Form.Row>


				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Incorporação dos restos vegetais e monitoramento</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data inicial - incorporação/destruição</Form.Label>
						{renderizadorData('dataInicioIncorporacao')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Data final - incorporação/destruição</Form.Label>
						{renderizadorData('dataTerminoIncorporacao')}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data de início do monitoramento</Form.Label>
						{renderizadorData('dataInicioMonitoramento')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Tempo de monitoramento a ser utilizado</Form.Label>
						<div className="d-flex" style={{ gap: '24px' }}>
							<Form.Check
								type="checkbox"
								ref={register}
								name="tempoMonitoramentoQuatroMeses"
								label="4 meses com irrigação"
							/>
							<Form.Check
								type="checkbox"
								ref={register}
								name="tempoMonitoramentoSeisMeses"
								label="6 meses sem irrigação"
							/>
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data prevista para término do monitoramento</Form.Label>
						{renderizadorData('dataPrevisaoTerminoMonitoramento')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Data de término do monitoramento</Form.Label>
						{renderizadorData('dataTerminoMonitoramento')}
					</Form.Group>
				</Form.Row>

				<p><small className="text-muted">* Em caso de necessidade de extensão do período de monitoramento, notifique a CIBio BASF (cibio@basf.com) e preencha o formulário correspondente.</small></p>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Formulário de limpeza de maquinários, equipamentos e/ou veículos utilizados</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold no-gutters">
					<Col md={2} className="px-1">Nome / Código</Col>
					<Col md={2} className="px-1">Entrada</Col>
					<Col md={2} className="px-1">Saída</Col>
					<Col md={2} className="px-1">Método de limpeza</Col>
					<Col md={3} className="px-1">Responsável</Col>
					<Col md={1} className="px-1" />
				</Row>

				{Array.from({ length: quantidadeLinhasLimpeza }).map((_, index) => {
					const metodoSelecionado = watch(`limpezaMetodo${index}` as any);
					return (
					<Row className="row-custom align-items-end no-gutters" key={`linha-limpeza-${index}`}>
						<Col md={2} className="px-1">
							<Form.Control ref={register} name={`limpezaNome${index}`} placeholder="Maquinário/Equipamento" />
						</Col>
						<Col md={2} className="px-1">
							{renderizadorData(`limpezaDataEntrada${index}`)}
						</Col>
						<Col md={2} className="px-1">
							{renderizadorData(`limpezaDataSaida${index}`)}
						</Col>
						<Col md={2} className="px-1">
							<MetodoLimpezaSelect
								register={register}
								name={`limpezaMetodo${index}`}
								nameOutros={`limpezaMetodoOutros${index}`}
								metodoSelecionado={metodoSelecionado}
							/>
						</Col>
						<Col md={3} className="px-1">
							<Form.Control ref={register} name={`limpezaResponsavel${index}`} placeholder="Responsável" />
						</Col>
						<Col md={1} className="d-flex justify-content-end px-1">
							{quantidadeLinhasLimpeza > 1 && (
								<Button
									ariaLabel="Remover"
									contained={false}
									type="button"
									className="btn-icon-only"
									onClick={() => removerLinhaLimpeza(index)}
								>
									<FaTrashAlt aria-hidden size={18} />
								</Button>
							)}
						</Col>
					</Row>
					);
				})}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar maquinário" contained={false} type="button" onClick={adicionarLinhaLimpeza} />
				</div>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Comunicação com a CIBio - colheita/destruição e monitoramento da LPMA</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre a colheita – Data de início</Form.Label>
						{renderizadorData('dataInicioComunicadoColheita')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre a colheita – Data de término</Form.Label>
						{renderizadorData('dataTerminoComunicadoColheita')}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre o monitoramento – Data de início</Form.Label>
						{renderizadorData('dataInicioComunicadoMonitoramento')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre o monitoramento – Data de término</Form.Label>
						{renderizadorData('dataTerminoComunicadoMonitoramento')}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Anexo — ofício(s) CIBio (PDF ou DOCX)</Form.Label>
						<Form.Control
							type="file"
							accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
							onChange={(e) => {
								const input = e.target as HTMLInputElement;
								const f = input.files?.[0];
								setErroArquivoComunicado1('');
								setRemoverAnexoComunicado1(false);
								if (!f) {
									setArquivoComunicadoForm1(null);
									return;
								}
								const err = validarArquivoPdfOuDocx(f);
								if (err) {
									setErroArquivoComunicado1(err);
									setArquivoComunicadoForm1(null);
									input.value = '';
									return;
								}
								setArquivoComunicadoForm1(f);
							}}
						/>
						{erroArquivoComunicado1 ? <small className="text-danger d-block mt-1">{erroArquivoComunicado1}</small> : null}
						{arquivoComunicadoForm1 ? (
							<small className="d-block text-muted mt-1">Selecionado: {arquivoComunicadoForm1.name}</small>
						) : null}
						{!arquivoComunicadoForm1 &&
						dadosColheita?.comunicadoCibioOficiosExternosNomeArquivo &&
						!removerAnexoComunicado1 ? (
							<div className="mt-2">
								<small className="text-muted">
									Arquivo atual: {dadosColheita.comunicadoCibioOficiosExternosNomeArquivo}
								</small>
								{idCadernoParaDownload > 0 && idColheitaEdicao > 0 ? (
									<>
										{' '}
										<Button
											type="button"
											contained={false}
											title="Baixar"
											onClick={() => void baixarAnexoComunicadoCibio()}
										/>
									</>
								) : null}
								{' '}
								<Button
									type="button"
									contained={false}
									title="Remover anexo"
									onClick={() => {
										setRemoverAnexoComunicado1(true);
										setArquivoComunicadoForm1(null);
									}}
								/>
							</div>
						) : null}
						{removerAnexoComunicado1 ? (
							<small className="text-warning d-block mt-1">O anexo será removido ao salvar.</small>
						) : null}
					</Form.Group>
				</Form.Row>

				<p>
					<small className="text-muted">
						IMPORTANTE: Anexar à pasta de campo (ABA COMUNICADOS EXTERNOS) a cópia do(s) ofício(s) da CIBio para CTNBio/MAPA com as informações sobre início e término da Colheita, início do Monitoramento e o e-mail de envio.
					</small>
				</p>
					</React.Fragment>
					)}

					{abaColheitaAtiva === 'colheita-checklist-1' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Checklist – Colheita e/ou destruição</h3>
						</Form.Label>
						<p>As perguntas abaixo são pontos de checagem visando o atendimento dos requisitos de biossegurança:</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="roupasColheita"
							label="a. Roupas, calçados e utensílios utilizados pela equipe foram limpos e inspecionados antes de entrar e antes de sair da área da LPMA?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacoesRoupasColheita"
					register={register}
					valorInicial={dadosColheita?.observacoesRoupasColheita}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="materialIdentificado"
							label="b. Em caso de colheita, o material colhido está devidamente identificado e duplamente embalado visando o transporte e/ou movimentação?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacoesMaterialIdentificado"
					register={register}
					valorInicial={dadosColheita?.observacoesMaterialIdentificado}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="equipamentosLimpos"
							label="c. Maquinários e equipamentos utilizados na colheita e/ou destruição foram limpos dentro da área da LPMA após o término das atividades?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacoesEquipamentosLimpos"
					register={register}
					valorInicial={dadosColheita?.observacoesEquipamentosLimpos}
				/>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesColheita"
							placeholder="Observações gerais da colheita/destruição"
						/>
					</Form.Group>
				</Form.Row>
					</React.Fragment>
					)}

					{abaColheitaAtiva === 'colheita-material-1' && (
					<React.Fragment>
				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Formulário – Registro de destino do Material Colhido</h3>
						</Form.Label>
						<p>Caderno de Conformidade de Campo (FCN) 64 — preencher apenas em caso de colheita.</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Formulário – Quantidade de material colhido e destino</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={2}>Data</Col>
					<Col md={2}>Talhão</Col>
					<Col md={3}>Ensaio/Experimento</Col>
					<Col md={2}>Nº Amostras</Col>
					<Col md={2}>Quantidade (Kg)</Col>
					<Col md={1}></Col>
				</Row>

				{Array.from({ length: quantidadeLinhasDestino }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`linha-destino-${index}`}>
						<Col md={2}>{renderizadorData(`destinoData${index}`)}</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`destinoTalhao${index}`} placeholder="Talhão" />
						</Col>
						<Col md={3}>
							<Form.Control ref={register} name={`destinoEnsaio${index}`} placeholder="Ensaio/Experimento" />
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`destinoNumeroAmostras${index}`} placeholder="Nº Amostras" />
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`destinoQuantidadeKg${index}`} placeholder="Quantidade" />
						</Col>
						<Col md={1} className="d-flex justify-content-end">
							{quantidadeLinhasDestino > 1 && (
								<Button
									ariaLabel="Remover"
									contained={false}
									type="button"
									className="btn-icon-only"
									onClick={() => removerLinhaDestino(index)}
								>
									<FaTrashAlt aria-hidden size={18} />
								</Button>
							)}
						</Col>
					</Row>
				))}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar material" contained={false} type="button" onClick={adicionarLinhaDestino} />
				</div>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label>Destinos</Form.Label>
						<Form.Check
							type="checkbox"
							ref={register}
							name="destinoMovimentacaoMesmaUnidade"
							label="Movimentação para análise/avaliação ou armazenamento no próprio site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<div className="d-flex" style={{ gap: '8px' }}>
								<Form.Control
									as="select"
									ref={register}
									name="destinoUnidadeMedidaMovimentacaoMesmaUnidade"
									defaultValue="kg"
									style={{ maxWidth: 120 }}
								>
									<option value="kg">Kg</option>
									<option value="gramas">Gramas</option>
									<option value="unidades">Unidades</option>
								</Form.Control>
								<Form.Control
									ref={register}
									name="destinoQuantidadeMovimentacaoMesmaUnidade"
									placeholder="Quantidade"
									style={{ maxWidth: 150 }}
								/>
							</div>
							{renderizadorAmiComLink('destinoAmiMovimentacaoMesmaUnidade', 'Nº AMI')}
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destinoEnvioOutraUnidade"
							label="Envio para análise/avaliação ou armazenamento em outro site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<div className="d-flex" style={{ gap: '8px' }}>
								<Form.Control
									as="select"
									ref={register}
									name="destinoUnidadeMedidaEnvioOutraUnidade"
									defaultValue="kg"
									style={{ maxWidth: 120 }}
								>
									<option value="kg">Kg</option>
									<option value="gramas">Gramas</option>
									<option value="unidades">Unidades</option>
								</Form.Control>
								<Form.Control
									ref={register}
									name="destinoQuantidadeEnvioOutraUnidade"
									placeholder="Quantidade"
									style={{ maxWidth: 150 }}
								/>
							</div>
							{renderizadorAmiComLink('destinoAmiEnvioOutraUnidade', 'Nº AMI', 200)}
						</div>
						<div className="mt-2 mb-2" style={{ maxWidth: 520 }}>
							<Form.Label>Carta de Transferência (PDF ou DOCX)</Form.Label>
							<Form.Control
								type="file"
								accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								onChange={(e) => {
									const input = e.target as HTMLInputElement;
									const f = input.files?.[0];
									setErroArquivoCartaTransferencia('');
									setRemoverAnexoCartaTransferencia(false);
									if (!f) {
										setArquivoCartaTransferencia(null);
										return;
									}
									const err = validarArquivoPdfOuDocx(f);
									if (err) {
										setErroArquivoCartaTransferencia(err);
										setArquivoCartaTransferencia(null);
										input.value = '';
										return;
									}
									setArquivoCartaTransferencia(f);
								}}
							/>
							{erroArquivoCartaTransferencia ? (
								<small className="text-danger d-block mt-1">{erroArquivoCartaTransferencia}</small>
							) : null}
							{arquivoCartaTransferencia ? (
								<small className="d-block text-muted mt-1">Selecionado: {arquivoCartaTransferencia.name}</small>
							) : null}
							{!arquivoCartaTransferencia &&
							dadosColheita?.cartaTransferenciaMaterialColhidoNomeArquivo &&
							!removerAnexoCartaTransferencia ? (
								<div className="mt-2">
									<small className="text-muted">
										Arquivo atual: {dadosColheita.cartaTransferenciaMaterialColhidoNomeArquivo}
									</small>
									{idCadernoParaDownload > 0 && idColheitaEdicao > 0 ? (
										<>
											{' '}
											<Button
												type="button"
												contained={false}
												title="Baixar"
												onClick={() => void baixarAnexoCartaTransferencia()}
											/>
										</>
									) : null}
									{' '}
									<Button
										type="button"
										contained={false}
										title="Remover anexo"
										onClick={() => {
											setRemoverAnexoCartaTransferencia(true);
											setArquivoCartaTransferencia(null);
										}}
									/>
								</div>
							) : null}
							{removerAnexoCartaTransferencia ? (
								<small className="text-warning d-block mt-1">A carta será removida ao salvar.</small>
							) : null}
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destinoDestruicaoAreaExperimental"
							label="Destruição na área experimental do próprio site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destinoQuantidadeAreaExperimental" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destinoDestruicaoAreaDescarteProprio"
							label="Destruição na área de descarte do próprio site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destinoQuantidadeAreaDescarteProprio" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
							{renderizadorAmiComLink('destinoAmiAreaDescarteProprio', 'Nº AMI')}
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destinoDestruicaoAreaOutroSite"
							label="Destruição na área de descarte em outro site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destinoDescricaoOutroSite" placeholder="*Especificar local" style={{ maxWidth: 250 }} />
							<Form.Control ref={register} name="destinoQuantidadeAreaOutroSite" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
							{renderizadorAmiComLink('destinoAmiAreaOutroSite', 'Nº AMI', 200)}
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destinoOutro"
							label="Outro (especificar)"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destinoOutroDescricao" placeholder="Especificar" style={{ maxWidth: 250 }} />
							<Form.Control ref={register} name="destinoOutroQuantidade" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
							{renderizadorAmiComLink('destinoOutroAmi', 'Nº AMI', 200)}
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observacoes e informações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesDestinoMaterialColhido"
							placeholder="Observações e informações adicionais"
						/>
					</Form.Group>
				</Form.Row>

				<p><small className="text-muted">* Anexar uma cópia da Autorização de Movimentação Interna (AMI) e/ou Carta de Transferência emitida pela CIBio na pasta de campo (ABA AMIs).</small></p>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Checklist - Material Colhido 1</h3>
						</Form.Label>
						<p>Checklist (preencher apenas o(s) item(s) aplicável(eis) ao destino do material)</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label><strong>1 MATERIAL COLHIDO – ENVIO/TRANSPORTE DE AMOSTRAS (outra localidade)</strong></Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialEnvioPrevistoCTNBio"
							label="a. O processo/LPMA aprovado pela CTNBio e/ou CIBio prevê o envio de amostras (material colhido)?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialEnvioPrevistoCTNBio"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialEnvioPrevistoCTNBio}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialEnvioAmiCarta"
							label="b. A Autorização de Movimentação Interna (AMI) e/ou Carta de Transferência está acompanhando o material?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialEnvioAmiCarta"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialEnvioAmiCarta}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialEnvioDuplaEmbalagem"
							label="c. As amostras estão identificadas e no mínimo duplamente embaladas?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialEnvioDuplaEmbalagem"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialEnvioDuplaEmbalagem}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialEnvioEmbalagemIdentificada"
							label="d. A embalagem de envio está identificada com as informações obrigatórias?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialEnvioEmbalagemIdentificada"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialEnvioEmbalagemIdentificada}
				/>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label><strong>2 MATERIAL COLHIDO PARA ARMAZENAMENTO (mesma localidade)</strong></Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialArmazenamentoPrevistoCTNBio"
							label="a. O processo/LPMA aprovado pela CTNBio e/ou CIBio prevê o armazenamento de amostras?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialArmazenamentoPrevistoCTNBio"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialArmazenamentoPrevistoCTNBio}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialArmazenamentoLocalCQB"
							label="b. O local de armazenamento possui CQB, acesso restrito e está devidamente identificado?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialArmazenamentoLocalCQB"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialArmazenamentoLocalCQB}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialArmazenamentoDuplaEmbalagem"
							label="c. A movimentação do material colhido para a sala de armazenamento foi realizada com dupla embalagem?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialArmazenamentoDuplaEmbalagem"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialArmazenamentoDuplaEmbalagem}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialArmazenamentoInventario"
							label="d. A quantidade de material armazenado foi registrada como entrada na cadeia de custódia ou inventário?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialArmazenamentoInventario"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialArmazenamentoInventario}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialArmazenamentoIdentificado"
							label="e. As amostras estão devidamente identificadas?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialArmazenamentoIdentificado"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialArmazenamentoIdentificado}
				/>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label><strong>3 MATERIAL COLHIDO E DESTRUÍDO</strong></Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialDestruidoProcedimentoCTNBio"
							label="a. A destruição e o descarte do material colhido foi realizada conforme procedimento previsto na LPMA aprovada pela CTNBio e/ou CIBio?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialDestruidoProcedimentoCTNBio"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialDestruidoProcedimentoCTNBio}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialDestruidoLocalCQB"
							label="b. O local da destruição e descarte do respectivo material possui CQB ativo, e está devidamente identificado?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialDestruidoLocalCQB"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialDestruidoLocalCQB}
				/>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterialDestruidoFormularioPreenchido"
							label="c. O Formulário de Destruição e Descarte foi devidamente preenchido?"
						/>
					</Form.Group>
				</Form.Row>
				<ComentarioExpandivel
					name="observacaoChecklistMaterialDestruidoFormularioPreenchido"
					register={register}
					valorInicial={dadosColheita?.observacaoChecklistMaterialDestruidoFormularioPreenchido}
				/>
					</React.Fragment>
					)}

					{/* Blocos Formulário 2, Checklist 2 e Material 2 removidos do fluxo de Colheita LPMA.
					{abaColheitaAtiva === 'colheita-form-2' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h2>Formulário de Colheita, Destruição e início de Monitoramento - 2</h2>
						</Form.Label>
						<p>Este formulário deve ser utilizado para registrar a colheita assim como ensaios não colhidos (destruídos).</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Registro dos envolvidos e datas inicial/final de colheita/destruição da LPMA</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data inicial/colheita</Form.Label>
						{renderizadorData('dataInicioColheita2')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Data final/colheita</Form.Label>
						{renderizadorData('dataTerminoColheita2')}
					</Form.Group>
				</Form.Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos2}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos2}
					nomesAdicionais={nomesAdicionais2}
					onNomesAdicionaisChange={setNomesAdicionais2}
					mostrarAssinatura={mostrarAssinatura2}
					onMostrarAssinaturaChange={setMostrarAssinatura2}
					assinaturasDisponiveis={assinaturasDisponiveis}
				/>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Detalhamento da colheita/destruição</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={2}>Talhão</Col>
					<Col md={3}>Ensaio/Experimento</Col>
					<Col md={2}>Situação</Col>
					<Col md={2}>Início</Col>
					<Col md={2}>Término</Col>
					<Col md={1}></Col>
				</Row>

				{Array.from({ length: quantidadeLinhasDetalhamento2 }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`linha-detalhamento2-${index}`}>
						<Col md={2}>
							<Form.Control ref={register} name={`detalhamento2Talhao${index}`} placeholder="Talhão" />
						</Col>
						<Col md={3}>
							<Form.Control ref={register} name={`detalhamento2Ensaio${index}`} placeholder="Ensaio/Experimento" />
						</Col>
						<Col md={2}>
							<Form.Control as="select" ref={register} name={`detalhamento2Status${index}`} defaultValue="">
								<option value="">Selecionar</option>
								<option value="1">1 - Colhido</option>
								<option value="2">2 - Destruído</option>
							</Form.Control>
						</Col>
						<Col md={2}>{renderizadorData(`detalhamento2DataInicio${index}`)}</Col>
						<Col md={2}>{renderizadorData(`detalhamento2DataTermino${index}`)}</Col>
						<Col md={1} className="d-flex justify-content-end">
							{quantidadeLinhasDetalhamento2 > 1 && (
								<Button
									ariaLabel="Remover"
									contained={false}
									type="button"
									className="btn-icon-only"
									onClick={() => removerLinhaDetalhamento2(index)}
								>
									<FaTrashAlt aria-hidden size={18} />
								</Button>
							)}
						</Col>
					</Row>
				))}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar linha" contained={false} type="button" onClick={adicionarLinhaDetalhamento2} />
				</div>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label>Tipo de colheita/destruição realizada</Form.Label>
						<div className="d-flex" style={{ gap: '24px' }}>
							<Form.Check type="checkbox" ref={register} name="tipoColheita2Mecanica" label="Mecânica" />
							<Form.Check type="checkbox" ref={register} name="tipoColheita2Manual" label="Manual" />
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Em caso de destruição antes da colheita, registre qual o estádio fenológico da cultura</Form.Label>
						<Form.Control
							ref={register}
							name="estadioFenologicoDestruicao2"
							placeholder="Descreva o estádio fenológico"
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Incorporação dos restos vegetais e início/término do monitoramento</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data inicial - incorporação/destruição dos restos vegetais</Form.Label>
						{renderizadorData('dataInicioIncorporacao2')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Data final - incorporação/destruição dos restos vegetais</Form.Label>
						{renderizadorData('dataTerminoIncorporacao2')}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data de início do monitoramento</Form.Label>
						{renderizadorData('dataInicioMonitoramento2')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Tempo de monitoramento a ser utilizado na LPMA</Form.Label>
						<div className="d-flex" style={{ gap: '24px' }}>
							<Form.Check type="checkbox" ref={register} name="tempoMonitoramento2QuatroMeses" label="4 meses com irrigação" />
							<Form.Check type="checkbox" ref={register} name="tempoMonitoramento2SeisMeses" label="6 meses sem irrigação" />
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>*Data prevista para o término do monitoramento</Form.Label>
						{renderizadorData('dataPrevisaoTerminoMonitoramento2')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Data de término do monitoramento</Form.Label>
						{renderizadorData('dataTerminoMonitoramento2')}
					</Form.Group>
				</Form.Row>

				<p>
					<small className="text-muted">
						*Em caso de necessidade de extensão do período de monitoramento deve-se notificar a CIBio BASF através do e-mail cibio@basf.com e preencher o formulário de extensão do período de monitoramento.
					</small>
				</p>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Formulário de limpeza de maquinários, equipamentos e/ou veículo utilizados na colheita/destruição e incorporação dos restos vegetais</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold no-gutters">
					<Col md={2} className="px-1">Nome / Código do maquinário/equipamento utilizado</Col>
					<Col md={2} className="px-1">Datas de limpeza - Antes ENTRADA</Col>
					<Col md={2} className="px-1">Datas de limpeza - Antes SAÍDA</Col>
					<Col md={2} className="px-1">Método de limpeza</Col>
					<Col md={3} className="px-1">Responsável(is)</Col>
					<Col md={1} className="px-1" />
				</Row>

				{Array.from({ length: quantidadeLinhasLimpeza2 }).map((_, index) => {
					const metodoSelecionado = watch(`limpeza2Metodo${index}` as any);
					return (
					<Row className="row-custom align-items-end no-gutters" key={`linha-limpeza2-${index}`}>
						<Col md={2} className="px-1">
							<Form.Control ref={register} name={`limpeza2Nome${index}`} placeholder="Maquinário/Equipamento" />
						</Col>
						<Col md={2} className="px-1">{renderizadorData(`limpeza2DataEntradaAntes${index}`)}</Col>
						<Col md={2} className="px-1">{renderizadorData(`limpeza2DataSaidaAntes${index}`)}</Col>
						<Col md={2} className="px-1">
							<MetodoLimpezaSelect
								register={register}
								name={`limpeza2Metodo${index}`}
								nameOutros={`limpeza2MetodoOutros${index}`}
								metodoSelecionado={metodoSelecionado}
							/>
						</Col>
						<Col md={3} className="px-1">
							<Form.Control ref={register} name={`limpeza2Responsavel${index}`} placeholder="Responsável" />
						</Col>
						<Col md={1} className="d-flex justify-content-end px-1">
							{quantidadeLinhasLimpeza2 > 1 && (
								<Button
									ariaLabel="Remover"
									contained={false}
									type="button"
									className="btn-icon-only"
									onClick={() => removerLinhaLimpeza2(index)}
								>
									<FaTrashAlt aria-hidden size={18} />
								</Button>
							)}
						</Col>
					</Row>
					);
				})}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar maquinário" contained={false} type="button" onClick={adicionarLinhaLimpeza2} />
				</div>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Comunicação com a CIBio - colheita/destruição e monitoramento da LPMA</h3>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre a atividade de colheita – Início</Form.Label>
						{renderizadorData('dataInicioComunicadoColheita2')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre a atividade de colheita – Término</Form.Label>
						{renderizadorData('dataTerminoComunicadoColheita2')}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre o monitoramento – Início</Form.Label>
						{renderizadorData('dataInicioComunicadoMonitoramento2')}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Comunicação à CIBio sobre o monitoramento – Término</Form.Label>
						{renderizadorData('dataTerminoComunicadoMonitoramento2')}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Anexo — ofício(s) CIBio (PDF ou DOCX)</Form.Label>
						<Form.Control
							type="file"
							accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
							onChange={(e) => {
								const input = e.target as HTMLInputElement;
								const f = input.files?.[0];
								setErroArquivoComunicado2('');
								setRemoverAnexoComunicado2(false);
								if (!f) {
									setArquivoComunicadoForm2(null);
									return;
								}
								const err = validarArquivoPdfOuDocx(f);
								if (err) {
									setErroArquivoComunicado2(err);
									setArquivoComunicadoForm2(null);
									input.value = '';
									return;
								}
								setArquivoComunicadoForm2(f);
							}}
						/>
						{erroArquivoComunicado2 ? <small className="text-danger d-block mt-1">{erroArquivoComunicado2}</small> : null}
						{arquivoComunicadoForm2 ? (
							<small className="d-block text-muted mt-1">Selecionado: {arquivoComunicadoForm2.name}</small>
						) : null}
						{!arquivoComunicadoForm2 &&
						dadosColheita?.comunicadoCibioOficiosExternos2NomeArquivo &&
						!removerAnexoComunicado2 ? (
							<div className="mt-2">
								<small className="text-muted">
									Arquivo atual: {dadosColheita.comunicadoCibioOficiosExternos2NomeArquivo}
								</small>
								{idCadernoParaDownload > 0 && idColheitaEdicao > 0 ? (
									<>
										{' '}
										<Button
											type="button"
											contained={false}
											title="Baixar"
											onClick={() => void baixarAnexoComunicadoCibio(2)}
										/>
									</>
								) : null}
								{' '}
								<Button
									type="button"
									contained={false}
									title="Remover anexo"
									onClick={() => {
										setRemoverAnexoComunicado2(true);
										setArquivoComunicadoForm2(null);
									}}
								/>
							</div>
						) : null}
						{removerAnexoComunicado2 ? (
							<small className="text-warning d-block mt-1">O anexo será removido ao salvar.</small>
						) : null}
					</Form.Group>
				</Form.Row>

				<p>
					<small className="text-muted">
						IMPORTANTE: Anexar na pasta de campo (ABA COMUNICADOS EXTERNOS) a cópia do(s) ofício(s) da CIBio para CTNBio/MAPA com as informações sobre início e término da Colheita, Início de Monitoramento e cópia do e-mail de envio(s).
					</small>
				</p>

				<p>
					<small className="text-muted">*Para quantidade de material colhido e destino preencher e/ou consultar o formulário de MATERIAL COLHIDO.</small>
				</p>
					</React.Fragment>
					)}

					{abaColheitaAtiva === 'colheita-checklist-2' && (
					<React.Fragment>
				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Checklist – Colheita e/ou destruição - 2</h3>
						</Form.Label>
						<p>As perguntas abaixo são pontos de checagem visando o atendimento dos requisitos de biossegurança:</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="roupasColheita2"
							label="a. Roupas, calçados e utensílios utilizados pela equipe foram limpos e inspecionados ANTES de entrar e ANTES de sair da área da LPMA?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacoesRoupasColheita2" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="materialIdentificado2"
							label="b. Em caso de colheita, o material colhido está devidamente identificado e duplamente embalado visando o transporte e/ou movimentação?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacoesMaterialIdentificado2" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="equipamentosLimpos2"
							label="c. Maquinários e equipamentos utilizados na colheita e/ou destruição foram limpos dentro da área da LPMA após o término das atividades?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacoesEquipamentosLimpos2" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>
					</React.Fragment>
					)}

					{abaColheitaAtiva === 'colheita-material-2' && (
					<React.Fragment>
				<Form.Row className="row-custom mt-5">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Formulário – Registro de destino do Material Colhido</h3>
						</Form.Label>
						<p><strong>Caderno de Conformidade de Campo (FCN) 69</strong></p>
						<p>Preencher este formulário somente em caso de colheita.</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Formulário – Quantidade de Material colhido e destino</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={2}>Data</Col>
					<Col md={2}>Talhão</Col>
					<Col md={3}>Ensaio/Experimento (s)</Col>
					<Col md={2}>Nº Amostras</Col>
					<Col md={2}>Quantidade (Kg)</Col>
					<Col md={1}></Col>
				</Row>

				{Array.from({ length: quantidadeLinhasDestino2 }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`linha-destino2-${index}`}>
						<Col md={2}>{renderizadorData(`destino2Data${index}`)}</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`destino2Talhao${index}`} placeholder="Talhão" />
						</Col>
						<Col md={3}>
							<Form.Control ref={register} name={`destino2Ensaio${index}`} placeholder="Ensaio/Experimento" />
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`destino2NumeroAmostras${index}`} placeholder="Nº Amostras" />
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`destino2QuantidadeKg${index}`} placeholder="Quantidade" />
						</Col>
						<Col md={1} className="d-flex justify-content-end">
							{quantidadeLinhasDestino2 > 1 && (
								<Button
									ariaLabel="Remover"
									contained={false}
									type="button"
									className="btn-icon-only"
									onClick={() => removerLinhaDestino2(index)}
								>
									<FaTrashAlt aria-hidden size={18} />
								</Button>
							)}
						</Col>
					</Row>
				))}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar material" contained={false} type="button" onClick={adicionarLinhaDestino2} />
				</div>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label>Destino(s): no destino poderá ser preenchido mais de uma opção.</Form.Label>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destino2MovimentacaoMesmaUnidade"
							label="Movimentação para análise/avaliação ou armazenamento no próprio site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<div className="d-flex" style={{ gap: '8px' }}>
								<Form.Control
									as="select"
									ref={register}
									name="destino2UnidadeMedidaMovimentacaoMesmaUnidade"
									defaultValue="kg"
									style={{ maxWidth: 120 }}
								>
									<option value="kg">Kg</option>
									<option value="gramas">Gramas</option>
									<option value="unidades">Unidades</option>
								</Form.Control>
								<Form.Control
									ref={register}
									name="destino2QuantidadeMovimentacaoMesmaUnidade"
									placeholder="Quantidade"
									style={{ maxWidth: 150 }}
								/>
							</div>
							<Form.Control ref={register} name="destino2AmiMovimentacaoMesmaUnidade" placeholder="Nº AMI" style={{ maxWidth: 200 }} />
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destino2EnvioOutraUnidade"
							label="Envio para análise/avaliação ou armazenamento em outro site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<div className="d-flex" style={{ gap: '8px' }}>
								<Form.Control
									as="select"
									ref={register}
									name="destino2UnidadeMedidaEnvioOutraUnidade"
									defaultValue="kg"
									style={{ maxWidth: 120 }}
								>
									<option value="kg">Kg</option>
									<option value="gramas">Gramas</option>
									<option value="unidades">Unidades</option>
								</Form.Control>
								<Form.Control
									ref={register}
									name="destino2QuantidadeEnvioOutraUnidade"
									placeholder="Quantidade"
									style={{ maxWidth: 150 }}
								/>
							</div>
							<Form.Control ref={register} name="destino2AmiEnvioOutraUnidade" placeholder="Nº AMI e/ou Carta de Transferência" style={{ maxWidth: 260 }} />
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destino2DestruicaoAreaExperimental"
							label="Destruição na área experimental do próprio site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destino2QuantidadeAreaExperimental" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destino2DestruicaoAreaDescarteProprio"
							label="Destruição na área de descarte do próprio site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destino2QuantidadeAreaDescarteProprio" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
							<Form.Control ref={register} name="destino2AmiAreaDescarteProprio" placeholder="Nº AMI" style={{ maxWidth: 200 }} />
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destino2DestruicaoAreaOutroSite"
							label="Destruição na área de descarte em outro site"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destino2DescricaoOutroSite" placeholder="*Especificar local" style={{ maxWidth: 250 }} />
							<Form.Control ref={register} name="destino2QuantidadeAreaOutroSite" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
							<Form.Control ref={register} name="destino2AmiAreaOutroSite" placeholder="Nº AMI e/ou Carta de Transferência" style={{ maxWidth: 260 }} />
						</div>

						<Form.Check
							type="checkbox"
							ref={register}
							name="destino2Outro"
							label="Outro (especificar)"
						/>
						<div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
							<Form.Control ref={register} name="destino2OutroDescricao" placeholder="Especificar" style={{ maxWidth: 250 }} />
							<Form.Control ref={register} name="destino2OutroQuantidade" placeholder="Quantidade/Kg" style={{ maxWidth: 150 }} />
							<Form.Control ref={register} name="destino2OutroAmi" placeholder="Nº AMI e/ou Carta de Transferência" style={{ maxWidth: 260 }} />
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações e informações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesDestinoMaterialColhido2"
							placeholder="Observações e informações adicionais"
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Checklist - Material Colhido 2</h3>
						</Form.Label>
						<p>1. Checklist (preencher apenas o(s) item(s) do checklist aplicável(eis) ao destino do material)</p>
						<p><strong>Caderno de Conformidade de Campo (FCN) 71</strong></p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label><strong>1 MATERIAL COLHIDO – ENVIO/TRANSPORTE DE AMOSTRAS (outra localidade)</strong></Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2EnvioPrevistoCTNBio"
							label="a. O processo/LPMA aprovado pela CTNBio e/ou CIBio prevê o envio de amostras (material colhido)?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2EnvioPrevistoCTNBio" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2EnvioAmiCarta"
							label="b. A Autorização de Movimentação Interna (AMI) e/ou Carta de Transferência está acompanhando o material?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2EnvioAmiCarta" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2EnvioDuplaEmbalagem"
							label="c. As amostras estão identificadas e no mínimo duplamente embaladas?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2EnvioDuplaEmbalagem" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2EnvioEmbalagemIdentificada"
							label="d. A embalagem de envio está identificada com: a) Nome, endereço e telefone do responsável pelo envio e recebimento do OGM; b) Etiqueta de Frágil e do símbolo universal de Risco Biológico; c) Mensagem: “O acesso ao conteúdo é restrito a equipe técnica devidamente capacitada”."
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2EnvioEmbalagemIdentificada" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label><strong>2 MATERIAL COLHIDO PARA ARMAZENAMENTO (mesma localidade)</strong></Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2ArmazenamentoPrevistoCTNBio"
							label="a. O processo/LPMA aprovado pela CTNBio e/ou CIBio prevê o armazenamento de amostras?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2ArmazenamentoPrevistoCTNBio" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2ArmazenamentoLocalCQB"
							label="b. O local de armazenamento possui CQB, acesso restrito e está devidamente identificado?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2ArmazenamentoLocalCQB" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2ArmazenamentoDuplaEmbalagem"
							label="c. A movimentação do material colhido para a sala de armazenamento foi realizada com dupla embalagem?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2ArmazenamentoDuplaEmbalagem" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2ArmazenamentoInventario"
							label="d. A quantidade de material armazenado foi registrada como entrada na cadeia de custódia ou inventário?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2ArmazenamentoInventario" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2ArmazenamentoIdentificado"
							label="e. As amostras estão devidamente identificadas?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2ArmazenamentoIdentificado" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-4">
					<Form.Group as={Col} md={12}>
						<Form.Label><strong>3 MATERIAL COLHIDO E DESTRUÍDO</strong></Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2DestruidoProcedimentoCTNBio"
							label="a. A destruição e o descarte do material colhido foi realizada conforme procedimento previsto na LPMA aprovada pela CTNBio e/ou CIBio?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2DestruidoProcedimentoCTNBio" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2DestruidoLocalCQB"
							label="b. O local da destruição e descarte do respectivo material possui CQB ativo, e está devidamente identificado?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2DestruidoLocalCQB" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="checklistMaterial2DestruidoFormularioPreenchido"
							label="c. O Formulário de Destruição e Descarte foi devidamente preenchido?"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						<Form.Control as="textarea" rows={2} ref={register} name="observacaoChecklistMaterial2DestruidoFormularioPreenchido" placeholder="Comentários" />
					</Form.Group>
				</Form.Row>
					</React.Fragment>
					)} */}

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

